import { useEffect, useState } from "react";
import { Flask, Star, LockSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../../lib/api";

const pct = (v) => (v == null ? "—" : `${v}%`);

function Experiment({ x, onChanged }) {
  const [busy, setBusy] = useState(false);
  const confident = x.confidence >= 0.95;
  const setDefault = async (variant) => {
    setBusy(true);
    try {
      await client.put(`/admin/marketing/experiments/${x.name}/default`, { variant });
      toast.success(variant ? `Everyone now sees: ${x.arms.find((a) => a.variant === variant)?.label}` : "50/50 test resumed");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={`experiment-${x.name}`} data-default={x.default || ""}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-white font-semibold">{x.title}</div>
          <div className="text-xs text-neutral-500">{x.question} <span className="text-neutral-600">· {x.note}</span></div>
        </div>
        {x.default ? (
          <button type="button" onClick={() => setDefault(null)} disabled={busy} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-neutral-300 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`experiment-${x.name}-resume`}>
            <ArrowCounterClockwise size={13} /> Resume 50/50 test
          </button>
        ) : null}
      </div>
      <div className={`rounded-lg p-3 my-3 text-sm ${x.default ? "bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-white" : confident ? "bg-[#00D4AA]/10 border border-[#00D4AA]/30 text-white" : "bg-white/[0.03] border border-white/[0.06] text-neutral-300"}`} data-testid={`experiment-${x.name}-verdict`}>
        {x.default
          ? <span className="inline-flex items-center gap-1.5"><LockSimple size={14} weight="fill" /> Locked: everyone sees <b>{x.arms.find((a) => a.variant === x.default)?.label}</b>. Test paused.</span>
          : x.verdict}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {x.arms.map((a) => (
          <div key={a.variant} className={`rounded-xl border p-3 ${x.leader === a.variant && !x.default ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.06]" : "border-white/[0.06] bg-black/20"}`} data-testid={`experiment-${x.name}-arm-${a.variant}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-white text-sm font-semibold truncate">{a.label} {x.leader === a.variant && !x.default && <Star size={13} weight="fill" className="inline text-[#FF5A1F] -mt-0.5" />}</div>
              <div className="text-[11px] text-neutral-500 whitespace-nowrap">{a.visitors} {x.mobile_only ? "mobile " : ""}visitors</div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              {[["Signups", a.signups, a.signup_rate], ["Cards", a.cards, a.card_rate], ["Paid", a.paid, null]].map(([k, n, r]) => (
                <div key={k} className="rounded-lg bg-black/40 p-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{k}</div>
                  <div className="text-base font-semibold text-white">{n}</div>
                  <div className="text-[10px] text-neutral-500 h-3">{r == null ? "" : pct(r)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-[11px] text-neutral-500">Bounce {pct(a.bounce_rate)} · card page {a.checkout_start}</div>
              {x.default !== a.variant && (
                <button type="button" onClick={() => setDefault(a.variant)} disabled={busy} className="text-[11px] text-[#FF7038] hover:text-[#FF5A1F] disabled:opacity-50" data-testid={`experiment-${x.name}-make-default-${a.variant}`}>
                  Make this the default
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExperimentsCard() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const load = () => client.get(`/admin/marketing/experiments?days=${days}`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [days]); // eslint-disable-line
  if (!d) return null;
  return (
    <div className="card-surface p-5" data-testid="experiments-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500"><Flask size={13} /> Start page A/B tests</div>
        <div className="flex items-center gap-1 text-xs">
          {[7, 30, 90].map((n) => (
            <button key={n} type="button" onClick={() => setDays(n)} className={`rounded-full px-2.5 py-1 ${days === n ? "bg-white/10 text-white" : "text-neutral-500 hover:text-white"}`} data-testid={`experiments-days-${n}`}>{n}d</button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {d.experiments.map((x) => <Experiment key={x.name} x={x} onChanged={load} />)}
      </div>
      <p className="text-[11px] text-neutral-600 mt-3">Each browser keeps the same version. Winner is judged on card adds once there are enough, signups until then. "Make this the default" locks it for everyone and pauses the test; "Resume" restarts the 50/50 split.</p>
    </div>
  );
}
